import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDraftInvoicesQuery } from '@/api';
import { useDeleteDraftInvoiceMutation } from '@/api/queries';
import type { ErrorResponse } from '@/api/handlers/responses';
import { PATH } from '@/router/PATH';
import type { ActiveDraft } from './activeDraftsMeta';

/**
 * Consumer contract for the `ActiveDrafts` widget. Wraps
 * `useDraftInvoicesQuery` (`/purchases/draft-invoices`) in the widget's
 * `{ data, error, isLoading, onRetry }` shape plus per-row `onEdit` and
 * `onDelete` adapters.
 *
 * Edit (row click or the dropdown action) resumes the draft in the
 * manual-import editor; Delete soft-deletes it server-side (the mutation
 * invalidates this list so the row drops out).
 */
export const useActiveDrafts = () => {
    const query = useDraftInvoicesQuery();
    const deleteMutation = useDeleteDraftInvoiceMutation();
    const navigate = useNavigate();

    // Keep the last successful page so a failed refetch doesn't wipe visible
    // rows (see usePendingInvoices).
    const lastItemsRef = React.useRef<ActiveDraft[]>([]);
    if (query.data?.success) {
        lastItemsRef.current = query.data.response.items;
    }

    const data = React.useMemo<ActiveDraft[]>(
        () =>
            query.data?.success
                ? query.data.response.items
                : lastItemsRef.current,
        [query.data],
    );

    // Gate the error while a retry is in flight (see usePendingInvoices) so
    // the boundary's Retry doesn't re-throw the stale failed envelope.
    const error = React.useMemo<ErrorResponse | null>(
        () =>
            !query.isFetching && query.data && !query.data.success
                ? (query.data.error ?? null)
                : null,
        [query.isFetching, query.data],
    );

    const onRetry = React.useCallback(() => {
        void query.refetch();
    }, [query]);

    const onEdit = React.useCallback(
        (draftId: string) =>
            navigate(PATH.PURCHASES.BIND_MANUAL_IMPORT_EDIT(draftId)),
        [navigate],
    );

    const onDelete = React.useCallback(
        (draftId: string) => {
            // Fire-and-forget: the envelope never rejects, and on success the
            // mutation invalidates this list so the row disappears.
            void deleteMutation.mutateAsync(draftId);
        },
        [deleteMutation],
    );

    return {
        data,
        error,
        isLoading:
            query.isPending || (query.isFetching && !query.data?.success),
        onDelete,
        onEdit,
        onRetry,
    } as const;
};
